import { useCallback, useEffect, useRef } from 'react';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import {
  toasterApi,
  extractPendingAskUserInteraction,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { useChatInstancesStore } from '../store/chatInstancesStore';
import { isActiveChatInstanceStatus } from '../utils/chatNetworkErrors';
import { prepareChatUserTurn } from './prepare-chat-user-turn';

export function useChatInstanceQueueDrain({
  chatId,
  chatMessages,
  status,
  isResumingStream,
  isWaitingForHITLReview,
  sendGraphMessage,
  handleAbort,
  cancelScheduledRecovery,
  intentionalAbortRef,
  pauseQueueDrainAfterAbortRef,
  clearNeoResumeSuppression,
  clearComposerLoadingSuppression,
}: {
  chatId: string;
  chatMessages: TerraUIMessage[];
  status: 'streaming' | 'submitted' | 'ready' | 'error';
  isResumingStream: boolean;
  isWaitingForHITLReview: boolean;
  sendGraphMessage: (message: TerraUIMessage) => Promise<void>;
  handleAbort: (args?: { pauseQueueDrain?: boolean }) => Promise<void>;
  cancelScheduledRecovery: () => void;
  intentionalAbortRef: React.RefObject<boolean>;
  pauseQueueDrainAfterAbortRef: React.RefObject<boolean>;
  clearNeoResumeSuppression: (chatId: string) => void;
  clearComposerLoadingSuppression: (chatId: string) => void;
}) {
  const tQueuedMessages = useTranslations('chat.copilot.queuedMessages');
  const {
    dequeueNextMessage,
    restoreQueuedMessage,
    getQueuedMessages,
    removeQueuedMessage,
  } = useChatInstancesStore();
  const isDrainingQueuedMessageRef = useRef(false);
  const blockedAutoDrainQueueItemIdRef = useRef<string | null>(null);
  const queuedMessages = getQueuedMessages({ chatId });

  useEffect(() => {
    if (isActiveChatInstanceStatus(status)) {
      pauseQueueDrainAfterAbortRef.current = false;
    }
  }, [chatId, pauseQueueDrainAfterAbortRef, status]);

  useEffect(() => {
    const askUserInteraction = extractPendingAskUserInteraction(chatMessages);
    const isAwaitingAskUserAnswer =
      askUserInteraction !== null &&
      askUserInteraction.selectedValue === undefined;

    if (
      status !== 'ready' ||
      isResumingStream ||
      isDrainingQueuedMessageRef.current ||
      pauseQueueDrainAfterAbortRef.current ||
      isWaitingForHITLReview ||
      isAwaitingAskUserAnswer
    ) {
      return;
    }

    if (queuedMessages.length === 0) {
      return;
    }

    const blockedAutoDrainQueueItemId = blockedAutoDrainQueueItemIdRef.current;

    if (blockedAutoDrainQueueItemId) {
      const blockedQueueItemIndex = queuedMessages.findIndex(
        (queueItem) => queueItem.id === blockedAutoDrainQueueItemId,
      );

      if (blockedQueueItemIndex === 0) {
        return;
      }

      if (blockedQueueItemIndex === -1) {
        blockedAutoDrainQueueItemIdRef.current = null;
      }
    }

    const nextQueuedMessage = dequeueNextMessage({ chatId });

    if (!nextQueuedMessage) {
      return;
    }

    isDrainingQueuedMessageRef.current = true;
    intentionalAbortRef.current = false;
    sendGraphMessage(nextQueuedMessage.message)
      .catch((queueDrainError) => {
        console.error('Failed to send queued message:', queueDrainError);
        blockedAutoDrainQueueItemIdRef.current = nextQueuedMessage.id;
        restoreQueuedMessage({
          chatId,
          queuedMessage: nextQueuedMessage,
          index: 0,
        });
        toasterApi.error({
          title: tQueuedMessages('sendNowErrorTitle'),
          description: tQueuedMessages('sendNowErrorDescription'),
        });
      })
      .finally(() => {
        isDrainingQueuedMessageRef.current = false;
      });
  }, [
    chatId,
    chatMessages,
    dequeueNextMessage,
    intentionalAbortRef,
    isResumingStream,
    isWaitingForHITLReview,
    pauseQueueDrainAfterAbortRef,
    queuedMessages,
    restoreQueuedMessage,
    sendGraphMessage,
    status,
    tQueuedMessages,
  ]);

  const sendQueuedMessageNow = useCallback(
    async ({ queueItemId }: { queueItemId: string }) => {
      const queuedMessagesForChat = getQueuedMessages({ chatId });
      const queueItemIndex = queuedMessagesForChat.findIndex(
        (queueItem) => queueItem.id === queueItemId,
      );
      const queuedMessage = queuedMessagesForChat[queueItemIndex];

      if (!queuedMessage) {
        return;
      }

      if (
        queuedMessage.state !== 'queued' ||
        getQueuedMessages({ chatId }).some(
          (queueItem) => queueItem.state === 'editing',
        )
      ) {
        return;
      }

      isDrainingQueuedMessageRef.current = true;
      removeQueuedMessage({ chatId, queueItemId });
      if (blockedAutoDrainQueueItemIdRef.current === queueItemId) {
        blockedAutoDrainQueueItemIdRef.current = null;
      }

      try {
        pauseQueueDrainAfterAbortRef.current = false;
        cancelScheduledRecovery();
        if (isActiveChatInstanceStatus(status) || isResumingStream) {
          await handleAbort({ pauseQueueDrain: false });
        }

        intentionalAbortRef.current = false;
        prepareChatUserTurn({
          chatId,
          clearNeoResumeSuppression,
          clearComposerLoadingSuppression,
        });
        await sendGraphMessage(queuedMessage.message);
      } catch (sendQueuedMessageError) {
        blockedAutoDrainQueueItemIdRef.current = queuedMessage.id;
        restoreQueuedMessage({
          chatId,
          queuedMessage,
          index: queueItemIndex,
        });

        throw sendQueuedMessageError;
      } finally {
        isDrainingQueuedMessageRef.current = false;
      }
    },
    [
      chatId,
      getQueuedMessages,
      handleAbort,
      removeQueuedMessage,
      cancelScheduledRecovery,
      restoreQueuedMessage,
      sendGraphMessage,
      status,
      isResumingStream,
      clearNeoResumeSuppression,
      clearComposerLoadingSuppression,
      intentionalAbortRef,
      pauseQueueDrainAfterAbortRef,
    ],
  );

  const clearBlockedAutoDrainForQueueItem = useCallback(
    (queueItemId: string) => {
      if (blockedAutoDrainQueueItemIdRef.current === queueItemId) {
        blockedAutoDrainQueueItemIdRef.current = null;
      }
    },
    [],
  );

  return {
    queuedMessages,
    sendQueuedMessageNow,
    clearBlockedAutoDrainForQueueItem,
  };
}
